import {
  useEffect,
  useState,
} from "react";
import {
  Eye,
  EyeOff,
} from "lucide-react";
import {
  useSearchParams,
} from "react-router-dom";

import {
  CountrySelect,
} from "./CountrySelect";
import {
  PhoneInput,
} from "./PhoneInput";
import {
  SegmentedDateInput,
} from "./SegmentedDateInput";

type Status = "loading" | "ready" | "submitting" | "done" | "error";

interface SecretFieldOption {
  value: string;
  label: string;
}

/**
 * One entry-form field. The render branches on a three-way precedence:
 * `options` (picker) > `inputType` (segmented date, or country + number) >
 * `sensitive` (masked text vs plain text).
 */
interface SecretField {
  /** Form field key; also the key used in the submitted values map. */
  key: string;
  /** Display label for the field. */
  label: string;
  /** Apply the `capitalize` class to the label (used for underscore-derived names). */
  capitalizeLabel?: boolean;
  /** Optional helper text shown under the input. */
  helpText?: string | null;
  /** Optional help link — caller MUST sanitize (e.g. http(s)-only) before passing. */
  helpUrl?: string | null;
  /**
   * `"date"`: a segmented Month/Day/Year entry. `"phone"`: a country picker
   * beside the number. `"country"`: a country picker submitting the ISO
   * two-letter code. Each takes precedence over `sensitive`.
   */
  inputType?: "date" | "phone" | "country";
  /** ISO region a `"phone"` field's country picker starts on. */
  defaultRegion?: string | null;
  /** Optional `max` attribute (e.g. today's date for a date-of-birth field). */
  max?: string;
  /** Optional `min` attribute (e.g. today's date for a document expiry). */
  min?: string;
  /** Closed choice set — rendered as a picker instead of a free-text input. */
  options?: SecretFieldOption[];
  /**
   * Mask the input (`type="password"`, with a show/hide toggle). Defaults to
   * masked when omitted so credential flows (API keys) stay hidden; vault PII
   * fields opt out by passing `false` so ordinary details (name, address) are
   * typed in the clear.
   */
  sensitive?: boolean;
  /**
   * The section this field belongs to (e.g. one per travelling companion on
   * the vault form). A section's fields must be CONSECUTIVE: its `title` is
   * rendered above the first of them, with a prefill slot scoped to the
   * section, and grouping is by `id` — two sections may share a title.
   * Fields without one render first, as before, with the top-level slot.
   */
  section?: SecretFieldSection;
}

export interface SecretFieldSection {
  id: string;
  title: string;
}

export interface SecretEntryLoaded {
  /** Heading shown above the form (may be derived from the loaded details). */
  title: string;
  /** One plain line under the heading, e.g. where the values are sent. */
  subtitle?: string | null;
  fields: SecretField[];
}

interface SecretEntryPageProps {
  loadDetails: (token: string) => Promise<SecretEntryLoaded>;
  submit: (token: string, values: Record<string, string>) => Promise<unknown>;
  /** Map a load failure to a user-facing message (preserves provider error detail). */
  mapLoadError: (err: unknown) => string;
  /** Map a save failure to a user-facing message. */
  mapSaveError: (err: unknown) => string;
  /**
   * Map a save failure to messages for the fields it names, keyed by field
   * `key`. Each named field is marked and shows its message under the input,
   * and the first is focused, so a refused value is found rather than read
   * about at the foot of a long form (GET-15087). Falls back to
   * `mapSaveError` when it names no field on the form.
   */
  mapFieldErrors?: (err: unknown) => Record<string, string>;
  description: string;
  submitLabel: string;
  doneBody: string;
  /**
   * Optional prefill affordance rendered above the form (e.g. the vault's
   * passport scanner), and again at the head of each `section` with only that
   * section's fields and its id. `applyValues` merges values for keys the form
   * actually collects and returns how many landed; the user still reviews and
   * submits.
   */
  prefillSlot?: (
    applyValues: (values: Record<string, string>) => number,
    fields: SecretField[],
    sectionId: string | null,
  ) => React.ReactNode;
}

/** The id of the control a field's label points at (a date's first segment). */
function inputIdOf(field: SecretField): string {
  return field.inputType === "date"
    ? `field-${field.key}-month`
    : `field-${field.key}`;
}

/**
 * Shared token-driven secret-entry page (custom-integration credentials and
 * vault secrets). Owns the load-by-token → form → submit → done/error
 * lifecycle; the provider-specific service calls, field model, and copy are
 * injected so the form scaffold lives in one place.
 */
export function SecretEntryPage({
  loadDetails,
  submit,
  mapLoadError,
  mapSaveError,
  mapFieldErrors,
  description,
  submitLabel,
  doneBody,
  prefillSlot,
}: SecretEntryPageProps) {
  const [searchParams] = useSearchParams();
  // Read once, on first render, because the effect below rewrites the address
  // bar out from under this. Both `?token=` routes (vault and custom
  // integrations) render through this component, so one capture covers both.
  const [token] = useState(() => searchParams.get("token") ?? "");

  // Take the token out of the visible URL as soon as it has been read
  // (security audit finding 17). The `sanitize_properties` hook in
  // `analytics/urlSanitizer.ts` already stops it reaching PostHog; this closes
  // the surfaces that hook cannot reach at all — browser history, the address
  // bar during a screen-share, and the `Referer` header on any later
  // navigation.
  //
  // The cost is that a refresh loses the token, since it now exists only in
  // this component's state. That is deliberate — the alternative is putting a
  // self-authenticating capability token into sessionStorage, which trades a
  // transient exposure for a persistent, XSS-readable one. These links are
  // single-use and expire within a day (vault links, GET-18350), so the
  // recovery is to re-open the original link while it lasts, which is what the
  // missing-token message says.
  useEffect(() => {
    if (!token) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has("token")) return;
    url.searchParams.delete("token");
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, [token]);

  const [status, setStatus] = useState<Status>("loading");
  const [loaded, setLoaded] = useState<SecretEntryLoaded | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Bumped per field a prefill fills, so that uncontrolled composite (a
  // segmented date, a phone with its country picker) remounts with the
  // applied value. Only those: a page-wide count also remounted the fields a
  // scan didn't touch, dropping a hand-picked country or a half-typed date.
  const [prefillNonce, setPrefillNonce] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!token) {
      setStatus("error");
      // Reachable by refreshing after the effect above clears the token from
      // the address bar, so this says what to do rather than what is absent.
      setError("This page needs the link you were sent. Open that link again to continue.");
      return;
    }

    (async () => {
      try {
        const res = await loadDetails(token);
        setLoaded(res);
        setValues(Object.fromEntries(res.fields.map((f) => [f.key, ""])));
        setStatus("ready");
      } catch (err) {
        setError(mapLoadError(err));
        setStatus("error");
      }
    })();
  }, [token]);

  const setValue = (key: string, value: string) => {
    setValues((v) => ({ ...v, [key]: value }));
    // An edited field is no longer the value the server refused.
    setFieldErrors((errs) => {
      if (!(key in errs)) return errs;
      const rest = { ...errs };
      delete rest[key];
      return rest;
    });
  };

  const applyPrefill = (incoming: Record<string, string>): number => {
    if (!loaded) return 0;
    const known = new Set(loaded.fields.map((f) => f.key));
    const entries = Object.entries(incoming).filter(
      ([key, value]) => known.has(key) && value,
    );
    if (entries.length > 0) {
      setValues((v) => ({ ...v, ...Object.fromEntries(entries) }));
      setPrefillNonce((n) => ({
        ...n,
        ...Object.fromEntries(entries.map(([key]) => [key, (n[key] ?? 0) + 1])),
      }));
    }
    return entries.length;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loaded) return;
    setStatus("submitting");
    setError(null);
    setFieldErrors({});
    try {
      await submit(token, values);
      setStatus("done");
    } catch (err) {
      const named = mapFieldErrors?.(err) ?? {};
      const marked: Record<string, string> = {};
      let first: SecretField | null = null;
      for (const field of loaded.fields) {
        const message = named[field.key];
        if (message === undefined) continue;
        marked[field.key] = message;
        first ??= field;
      }
      if (first === null) {
        setError(mapSaveError(err));
      } else {
        const count = Object.keys(marked).length;
        setFieldErrors(marked);
        setError(
          count === 1
            ? "One detail needs another look. It's marked above."
            : `${count} details need another look. They're marked above.`,
        );
        document.getElementById(inputIdOf(first))?.focus();
      }
      setStatus("ready");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white shadow-soft-lg rounded-2xl p-8">
        {status === "loading" && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-catch-red mx-auto mb-4" />
            <p className="text-gray-600">Loading…</p>
          </div>
        )}

        {status === "error" && (
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              We couldn't open this link
            </h2>
            <p className="text-gray-600">{error}</p>
          </div>
        )}

        {(status === "ready" || status === "submitting") && loaded && (
          <>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {loaded.title}
            </h2>
            {loaded.subtitle && (
              <p className="text-sm font-medium text-gray-900 mb-2">
                {loaded.subtitle}
              </p>
            )}
            <p className="text-sm text-gray-600 mb-6">{description}</p>
            {prefillSlot?.(
              applyPrefill,
              loaded.fields.filter((f) => f.section === undefined),
              null,
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              {loaded.fields.map((field, index) => {
                const inputId = inputIdOf(field);
                const fieldError = fieldErrors[field.key];
                // Set only while the server's refusal of this field stands.
                const errorId = fieldError ? `error-${field.key}` : undefined;
                // A section heads its first field, with its own prefill slot
                // scoped to the section's fields.
                const section = field.section;
                const startsSection =
                  section !== undefined &&
                  section.id !== loaded.fields[index - 1]?.section?.id;
                return (
                <div key={field.key}>
                  {section && startsSection && (
                    <div className="border-t border-gray-200 pt-4 mt-6">
                      <h3 className="text-base font-semibold text-gray-900 mb-3">
                        {section.title}
                      </h3>
                      {prefillSlot?.(
                        applyPrefill,
                        loaded.fields.filter((f) => f.section?.id === section.id),
                        section.id,
                      )}
                    </div>
                  )}
                  <label
                    // A radio group has no single input to point at; it is
                    // labelled via aria-labelledby instead.
                    htmlFor={field.options ? undefined : inputId}
                    id={`label-${field.key}`}
                    className={`block text-sm font-medium text-gray-700 mb-1${field.capitalizeLabel ? " capitalize" : ""}`}
                  >
                    {field.label}
                  </label>

                  {field.options ? (
                    <div
                      role="radiogroup"
                      aria-labelledby={`label-${field.key}`}
                      aria-invalid={errorId ? true : undefined}
                      aria-describedby={errorId}
                      className="flex gap-2"
                    >
                      {field.options.map((option, optionIndex) => {
                        const selected = values[field.key] === option.value;
                        return (
                          <label
                            key={option.value}
                            className={`flex-1 cursor-pointer rounded-md border px-3 py-2 text-center text-sm font-medium transition-colors focus-within:ring-2 focus-within:ring-catch-red focus-within:ring-offset-1 ${
                              selected
                                ? "border-catch-red bg-catch-red/10 text-catch-red"
                                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            <input
                              // The first radio carries the field's id, so a
                              // refused choice can be focused like any input.
                              id={optionIndex === 0 ? inputId : undefined}
                              type="radio"
                              name={`field-${field.key}`}
                              value={option.value}
                              checked={selected}
                              required
                              onChange={() =>
                                setValue(field.key, option.value)
                              }
                              className="sr-only"
                            />
                            {option.label}
                          </label>
                        );
                      })}
                    </div>
                  ) : field.inputType === "phone" ? (
                    <PhoneInput
                      key={`${field.key}-${prefillNonce[field.key] ?? 0}`}
                      id={`field-${field.key}`}
                      defaultRegion={field.defaultRegion}
                      initialValue={values[field.key]}
                      onChange={(value) => setValue(field.key, value)}
                      errorId={errorId}
                    />
                  ) : field.inputType === "country" ? (
                    <CountrySelect
                      id={inputId}
                      value={values[field.key]}
                      onChange={(code) => setValue(field.key, code)}
                      errorId={errorId}
                    />
                  ) : field.inputType === "date" ? (
                    <SegmentedDateInput
                      key={`${field.key}-${prefillNonce[field.key] ?? 0}`}
                      id={`field-${field.key}`}
                      max={field.max}
                      min={field.min}
                      initialIso={values[field.key]}
                      onChange={(iso) => setValue(field.key, iso)}
                      errorId={errorId}
                    />
                  ) : (
                    <div className="relative">
                      <input
                        id={`field-${field.key}`}
                        type={
                          field.sensitive === false || revealed[field.key]
                            ? "text"
                            : "password"
                        }
                        autoComplete="off"
                        required
                        aria-invalid={errorId ? true : undefined}
                        aria-describedby={errorId}
                        value={values[field.key] ?? ""}
                        onChange={(e) => setValue(field.key, e.target.value)}
                        className={`block w-full rounded-md ${errorId ? "border-red-400" : "border-gray-300"} shadow-sm focus:outline-none focus:ring-2 focus:border-catch-red focus:ring-catch-red px-3 py-2 border${field.sensitive === false ? "" : " pr-10"}`}
                      />
                      {field.sensitive !== false && (
                        <button
                          type="button"
                          onClick={() =>
                            setRevealed((r) => ({
                              ...r,
                              [field.key]: !r[field.key],
                            }))
                          }
                          aria-label={
                            revealed[field.key] ? "Hide value" : "Show value"
                          }
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                        >
                          {revealed[field.key] ? (
                            <Eye className="h-5 w-5" aria-hidden="true" />
                          ) : (
                            <EyeOff className="h-5 w-5" aria-hidden="true" />
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {fieldError && (
                    <p id={errorId} className="mt-1 text-sm text-red-600">
                      {fieldError}
                    </p>
                  )}

                  {(field.helpText || field.helpUrl) && (
                    <p className="mt-1 text-xs text-gray-500">
                      {field.helpText}
                      {field.helpText && field.helpUrl ? " " : null}
                      {field.helpUrl && (
                        <a
                          href={field.helpUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-catch-red underline hover:opacity-80"
                        >
                          View setup guide →
                        </a>
                      )}
                    </p>
                  )}
                </div>
                );
              })}
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full rounded-xl bg-catch-red px-4 py-3 font-medium text-white hover:bg-catch-red/90 disabled:opacity-50"
              >
                {status === "submitting" ? "Saving…" : submitLabel}
              </button>
            </form>
          </>
        )}

        {status === "done" && (
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">All set</h2>
            <p className="text-gray-600">{doneBody}</p>
          </div>
        )}
      </div>
    </div>
  );
}
